import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, PlugZap, Download, KeyRound } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";

export const isCuelinksName = (name) => /cue\s*-?\s*links?/i.test(name || "");

export const CuelinksNotice = ({ onClose }) => (
  <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="cuelinks-builtin-notice">
    <b>Cuelinks is already built in</b> - you don't need to add it here. Close this form and use the "Cuelinks - built in" card at the top of Networks.
    Your key is managed in <Link to="/admin/integrations" onClick={onClose} className="underline font-semibold">Integrations & Keys</Link>.
  </div>
);

const useKeyState = () => useQuery({
  queryKey: ["integration-keys"],
  queryFn: () => api.get("/admin/integration-keys").then((r) => r.data),
  select: (d) => (d.find?.((g) => g.key === "cuelinks") || (d.groups || []).find((g) => g.key === "cuelinks"))?.fields?.find((f) => f.name === "CUELINKS_API_KEY"),
});

export const CuelinksCard = () => {
  const { data: key } = useKeyState();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const test = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/cuelinks/test"); setResult({ ok: true, ...r.data }); toast.success("Cuelinks connection works"); }
    catch (e) { setResult({ ok: false, error: errMsg(e) }); toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const connected = key?.is_set;
  return (
    <div className="mb-5 rounded-xl border border-slate-200 bg-white p-5" data-testid="cuelinks-builtin-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <p className="font-bold text-slate-900">Cuelinks <span className="ml-1 text-xs font-semibold text-slate-500">built in</span></p>
          <p className="text-sm mt-0.5 flex items-center gap-1.5" data-testid="cuelinks-key-status">
            {connected ? <><CheckCircle2 size={15} className="text-emerald-600" /> API key saved{key.hint ? ` (${key.hint})` : ""}</> : <><AlertCircle size={15} className="text-amber-600" /> No API key saved yet</>}
          </p>
        </div>
        <Link to="/admin/integrations" className="px-3 py-2 rounded-lg border text-sm font-semibold inline-flex items-center gap-1.5" data-testid="cuelinks-edit-key-link"><KeyRound size={15} /> {connected ? "Change key" : "Add key"}</Link>
        <button onClick={test} disabled={busy || !connected} className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="cuelinks-test-button"><PlugZap size={15} /> {busy ? "Testing…" : "Test connection"}</button>
        <Link to="/admin/import" className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1.5" data-testid="cuelinks-import-link"><Download size={15} /> Import offers</Link>
      </div>
      {result && (
        <div className={`mt-3 rounded-lg p-3 text-sm ${result.ok ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-800"}`} data-testid="cuelinks-test-result">
          {result.ok ? (
            <>Connected. Live on your site from Cuelinks: {result.live_coupons} coupons/offers, {result.live_deals} deals.{result.last_import && ` Last import ${fmtDate(result.last_import.finished_at)}.`}
              {result.sample?.length > 0 && <> Latest offers: {result.sample.map((s) => `${s.title} (${s.store})`).join(" · ")}</>}</>
          ) : result.error}
        </div>
      )}
    </div>
  );
};
